/* global gdAlertify */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axiosInstance from '../config/axiosConfig';
import BuscarPaciente from './components/BuscarPaciente';
import BuscarAuxiliar from './components/BuscarAuxiliar';
import Select from 'react-select';
import { calculateAge, formatFechaES } from '../utils/utils';

const ClearableLabel = ({ htmlFor, children, onClear, disabled = false }) => (
    <div className="d-flex align-items-center justify-content-between">
        <label htmlFor={htmlFor} className="form-label mb-1">{children}</label>
        <button
            type="button"
            className="btn btn-link btn-sm text-muted text-decoration-none p-0 mb-1"
            onClick={onClear}
            disabled={disabled}
        >
            Limpiar
        </button>
    </div>
);

const FormVisita = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const [formData, setFormData] = useState({
        visitaID: 0,
        pacienteID: '',
        fechaVisita: '',
        puedeModificarFecha: true,
        tipoVisita: '',
        diagnostico: '',
        estado_salud: '',
        estado_cognitivo: '',
        habla: '',
        movimiento: '',
        antecedentes_medicos: '',
        examen_fisico: '',
        medicamento_horarios: '',
        rutina: '',
        plan_atencion: '',
        observaciones_paciente: '',
        visita_fuera_santiago: 0,
        auxiliarID: '',
        observacion_auxiliar: '',
    });

    const [pacienteInfo, setPacienteInfo] = useState(null);
    const [auxiliares, setAuxiliares] = useState([]);
    const [auxiliarOption, setAuxiliarOption] = useState(null);
    const [modoAuxiliar, setModoAuxiliar] = useState('turno'); // 'turno' | 'libre'
    const [tipoVisita] = useState([{ value: 1, label: "Presencial" }, { value: 3, label: "Presencial Fuera de Santiago" }, { value: 2, label: "Telefonico" }]);

    useEffect(() => {
        if (id && id != undefined) {
            fetchVisita(id);
        } else {
            const pacienteIDParam = searchParams.get('pacienteID');
            if (pacienteIDParam) {
                fetchPacienteById(pacienteIDParam);
            }
        }
    }, [id]);

    const fetchPacienteById = async (pacienteID) => {
        try {
            gdAlertify.showLoader();
            const { data } = await axiosInstance.get('/paciente/busqueda', {
                params: { id: pacienteID, conAuxiliares: true, conVisita: true }
            });
            if (data.data && data.data.length > 0) {
                handleSeleccionPaciente(data.data[0]);
            }
        } catch (error) {
            console.error('Error al cargar paciente:', error);
        } finally {
            gdAlertify.hideLoader();
        }
    };

    const fetchVisita = async (visitaId) => {
        try {
            gdAlertify.showLoader();
            const { data } = await axiosInstance.post(`/paciente/ver-visita`, { visitaID: visitaId });

            const visita = data.data;
            setFormData({
                visitaID: (id && id != undefined) ? visita.visitaID || 0 : 0,
                pacienteID: visita.pacienteID || '',
                fechaVisita: visita.fecha_visita || '',
                puedeModificarFecha: visita.puedeModificarFecha ?? true,
                tipoVisita: visita.tipo_visita || '',
                diagnostico: visita.diagnostico || '',
                estado_salud: visita.estado_salud || '',
                estado_cognitivo: visita.estado_cognitivo || '',
                habla: visita.habla || '',
                movimiento: visita.movimiento || '',
                antecedentes_medicos: visita.antecedentes_medicos || '',
                examen_fisico: visita.examen_fisico || '',
                medicamento_horarios: visita.medicamento_horarios || '',
                rutina: visita.rutina || '',
                plan_atencion: visita.plan_atencion || '',
                observaciones_paciente: visita.observacion || '',
                visita_fuera_santiago: visita.visita_fuera_santiago || '',
                auxiliarID: visita.auxiliar_id || '',
                observacion_auxiliar: visita.observacion_auxiliar || '',
            });

            if (visita.paciente) {
                setPacienteInfo(visita.paciente);
                const opts = (visita.paciente.auxiliares || []).map(aux => ({
                    value: aux.id,
                    label: `${aux.nombre} ${aux.apellidoPaterno} ${aux.apellidoMaterno} (${aux.rut})`
                }));
                setAuxiliares(opts);
                if (visita.auxiliar_id) {
                    const existing = opts.find(o => o.value === visita.auxiliar_id);
                    if (existing) {
                        setAuxiliarOption(existing);
                    } else {
                        setModoAuxiliar('libre');
                    }
                }
            }
        } catch (error) {
            gdAlertify.alertError("Error al cargar la visita.");
        } finally {
            gdAlertify.hideLoader();
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const clearFormField = (fieldName) => {
        setFormData((prev) => ({
            ...prev,
            [fieldName]: '',
            ...(fieldName === 'tipoVisita' ? { visita_fuera_santiago: '' } : {}),
        }));
    };

    const handleSelect = (selectedOption) => {
        const tipo = selectedOption ? selectedOption.value : "";
        setFormData((prev) => ({
            ...prev,
            tipoVisita: tipo,
            visita_fuera_santiago: tipo === 1 ? (prev.visita_fuera_santiago || '0') : '',
        }));
    };

    const handleSeleccionPaciente = (paciente) => {
        setPacienteInfo(paciente);
        setFormData((prev) => ({
            ...prev,
            ...paciente.visita,
            pacienteID: paciente.id ? paciente.id : "",
            diagnostico: paciente.diagnostico,
            visitaID: (id && id != undefined) ? paciente.visita.visitaID || 0 : 0,
            auxiliarID: '',
            observacion_auxiliar: '',
        }));
        setAuxiliarOption(null);
        setAuxiliares(paciente.auxiliares.map(aux => ({
            value: aux.id,
            label: `${aux.nombre} ${aux.apellidoPaterno} ${aux.apellidoMaterno} (${aux.rut})`
        })));
    };

    const handleAuxiliarSelect = (selectedOption) => {
        setAuxiliarOption(selectedOption);
        setFormData((prev) => ({
            ...prev,
            auxiliarID: selectedOption ? selectedOption.value : '',
        }));
    };

    const handleAuxiliarLibre = (auxiliar) => {
        if (!auxiliar) {
            setFormData((prev) => ({ ...prev, auxiliarID: '' }));
            return;
        }
        setFormData((prev) => ({ ...prev, auxiliarID: auxiliar.id }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (formData.tipoVisita == '') {
            gdAlertify.alertError('Falta Tipo de visita');
            return;
        }

        gdAlertify.confirm({
            title: 'Confirmar Ingreso',
            message: `¿Está seguro de guardar el ingreso para el dia ${formatFechaES(formData.fechaVisita)}?`,
            onConfirm: async () => {
                try {
                    gdAlertify.showLoader();
                    axiosInstance.post('/paciente/visita/registro', { visita: formData })
                        .then((response) => {
                            if (!response.data.error) {
                                gdAlertify.alertSuccess(response.data.message);
                            } else {
                                gdAlertify.alertError(response.data.message);
                            }
                        })
                        .catch((error) => {
                            gdAlertify.notiError(error);
                            console.error(error);
                        });
                    gdAlertify.hideLoader();
                } catch (error) {
                    gdAlertify.hideLoader();
                    gdAlertify.notiError("Ocurrió un error al conectar con el servidor.");
                }
            },
            onCancel: () => {
                gdAlertify.notify('Envío cancelado', 'error');
            }
        });
    };

    return (
        <div className="container mt-4">
            <div className="card">
                <div className="card-header">
                    <h3>Formulario de Visita</h3>
                </div>
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        <fieldset>
                            <div className="mb-3">
                                <BuscarPaciente
                                    onSelect={handleSeleccionPaciente}
                                    conAuxiliares={true}
                                    conVisita={true}
                                />
                            </div>

                            <div className="mb-3">
                                {pacienteInfo ? (
                                    <div>
                                        <strong>Paciente Seleccionado:</strong> {pacienteInfo.nombres} {pacienteInfo.apellidoPaterno} {pacienteInfo.apellidoMaterno}<br />
                                        <strong>Edad:</strong> {calculateAge(pacienteInfo.fechaNacimiento)} Años
                                    </div>
                                ) : (
                                    <p>No se ha seleccionado un paciente.</p>
                                )}
                            </div>

                            {[
                                { name: "fechaVisita", label: "Fecha de Visita", required: true, type: "date" },
                                { name: "tipoVisita", label: "Tipo de Visita", required: false, type: "select", options: tipoVisita },
                            ].map((field) => (
                                <div className="mb-3" key={field.name}>
                                    <ClearableLabel
                                        htmlFor={field.name}
                                        onClear={() => clearFormField(field.name)}
                                        disabled={field.name === 'fechaVisita' && !formData.puedeModificarFecha}
                                    >
                                        {field.label}
                                    </ClearableLabel>
                                    {field.type === "select" ? (
                                        <Select
                                            options={field.options}
                                            value={field.options.find(opt => opt.value === formData[field.name])}
                                            onChange={handleSelect}
                                            placeholder="Seleccione"
                                            isClearable
                                        />
                                    ) : (
                                        <input
                                            type={field.type || "text"}
                                            id={field.name}
                                            name={field.name}
                                            value={formData[field.name]}
                                            onChange={handleChange}
                                            className="form-control"
                                            disabled={field.name === 'fechaVisita' && !formData.puedeModificarFecha}
                                            required={field.required}
                                        />
                                    )}
                                </div>
                            ))}

                            {[
                                { name: "diagnostico", label: "Diagnóstico", required: false, type: "textarea" },
                                { name: "estado_salud", label: "Estado de salud", required: false, type: "textarea" },
                                { name: "estado_cognitivo", label: "Estado cognitivo", required: false, type: "textarea" },
                                { name: "habla", label: "Lenguaje", required: false, type: "textarea" },
                                { name: "movimiento", label: "Movilidad", required: false, type: "textarea" },
                                { name: "antecedentes_medicos", label: "Antecedentes medicos", required: false, type: "textarea" },
                                { name: "examen_fisico", label: "Evolución", required: false, type: "textarea" },
                                { name: "medicamento_horarios", label: "Medicamentos y horario", required: false, type: "textarea" },
                                { name: "rutina", label: "Rutinas y/o actividades del paciente", required: false, type: "textarea" },
                                { name: "plan_atencion", label: "Plan de atencion", required: false, type: "textarea" },
                                { name: "observaciones_paciente", label: "Observacion paciente", required: false, type: "textarea" },
                            ].map((field) => (
                                <div className="mb-3" key={field.name}>
                                    <ClearableLabel htmlFor={field.name} onClear={() => clearFormField(field.name)}>
                                        {field.label}
                                    </ClearableLabel>
                                    <textarea
                                        id={field.name}
                                        name={field.name}
                                        value={formData[field.name]}
                                        onChange={handleChange}
                                        className="form-control"
                                        required={field.required}
                                    />
                                </div>
                            ))}

                            {/* Auxiliar + Observacion */}
                            <div className="mb-3">
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                    <label className="form-label mb-0">Auxiliar</label>
                                    <div className="btn-group btn-group-sm">
                                        <button
                                            type="button"
                                            className={`btn ${modoAuxiliar === 'turno' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => { setModoAuxiliar('turno'); handleAuxiliarSelect(null); }}
                                        >
                                            En turno / acuerdo
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn ${modoAuxiliar === 'libre' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => { setModoAuxiliar('libre'); handleAuxiliarSelect(null); }}
                                        >
                                            Sin turno
                                        </button>
                                    </div>
                                </div>
                                {modoAuxiliar === 'turno' ? (
                                    <Select
                                        options={auxiliares}
                                        value={auxiliarOption}
                                        onChange={handleAuxiliarSelect}
                                        placeholder="Seleccione un auxiliar"
                                        isClearable
                                    />
                                ) : (
                                    <BuscarAuxiliar onSelect={handleAuxiliarLibre} />
                                )}
                            </div>

                            <div className="mb-3">
                                <ClearableLabel htmlFor="observacion_auxiliar" onClear={() => clearFormField('observacion_auxiliar')}>
                                    Observación auxiliar
                                </ClearableLabel>
                                <textarea
                                    id="observacion_auxiliar"
                                    name="observacion_auxiliar"
                                    value={formData.observacion_auxiliar}
                                    onChange={handleChange}
                                    className="form-control"
                                />
                            </div>

                            <div className="text-center mt-3">
                                <button type="submit" className="btn btn-success">Guardar</button>
                            </div>
                        </fieldset>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default FormVisita;
